import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryPointDetails } from '../types/delivery-point-details.type';
import { UpdateDeliveryPointRequestDTO } from '../types/dtos/update-delivery-point.dto';
import { ADMIN_DELIVERY_POINTS_API_PATHS } from '../constants/delivery-points-api-paths.constants';

type UpdateDeliveryPointParams = {
  deliveryPointId: string;
  deliveryPointData: UpdateDeliveryPointRequestDTO;
};

type UseUpdateDeliveryPointResult = {
  updateDeliveryPoint: (params: UpdateDeliveryPointParams) => Promise<DeliveryPointDetails | null>;
  isSubmitting: boolean;
};

export function useUpdateDeliveryPoint(): UseUpdateDeliveryPointResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateDeliveryPoint = async ({
    deliveryPointId,
    deliveryPointData,
  }: UpdateDeliveryPointParams): Promise<DeliveryPointDetails | null> => {
    setIsSubmitting(true);

    try {
      const updatedDeliveryPoint = await HttpClient.patch<DeliveryPointDetails, UpdateDeliveryPointRequestDTO>(
        ADMIN_DELIVERY_POINTS_API_PATHS.UPDATE_DELIVERY_POINT(deliveryPointId),
        deliveryPointData
      );

      enqueueSnackbar('Point de livraison mis à jour avec succès', { variant: 'success' });
      return updatedDeliveryPoint;
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la mise à jour du point de livraison'),
        { variant: 'error' }
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { updateDeliveryPoint, isSubmitting };
}
